import PropTypes from "prop-types";
import React, { useState, useCallback, memo, useEffect } from "react";
import styled from "styled-components";

// スタイル定義
const ListItemBase = styled.div`
  width: 100%;
  height: ${({ $size }) => $size === "compact" ? "40px" : "56px"};
  padding: ${({ $size }) => $size === "section" ? "0px 0px 4px 16px" : "0px 0px 0px 16px"};
  display: flex;
  align-items: ${({ $size }) => $size === "section" ? "flex-end" : "center"};
  justify-content: space-between;
  border-bottom: ${({ $hasBorderBottom }) =>
    $hasBorderBottom ? "1px solid var(--soremo-border)" : "none"};
`;

const InteractiveListItem = styled(ListItemBase)`
  background-color: ${({ $isActive, $isHover, $isSelected }) =>
    $isSelected ? "var(--soremo-border)" :
    (!$isSelected && $isActive) ? "var(--soremo-border)" :
    (!$isSelected && $isHover) ? "var(--soremo-background)" : "white"};
  cursor: ${({ $isSelected }) => $isSelected ? "default" : "pointer"};
`;

const SectionListItem = styled(ListItemBase).attrs({
  className: "u-row bodytext-13 u-text-light-gray",
})``;

// chevron 非表示（非インタラクティブ）時に、chevron(24px) + Status の gap(4px) 分の
// 右パディングを確保し、status 表示位置をインタラクティブ時と揃える。
const NonInteractiveListItem = styled(ListItemBase)`
  padding-right: 28px;
  cursor: default;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $hasAlert }) => $hasAlert ? "4px" : "0"};
`;

const Label = styled.div.attrs({
  className: "heading-spacing",
})`
  line-height: 100%;
`;

const AlertText = styled.div.attrs({
  className: "bodytext-11",
})`
  line-height: 100%;
  color: var(--soremo-green);
`;

const Status = styled.div.attrs({
  className: "bodytext-13",
})`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ $statusTone }) =>
    $statusTone === "accent" ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
`;

const Icon = styled.div.attrs({
  className: "material-symbols-rounded u-fontsize-24",
})`
  color: ${({ $isHover, $isSelected }) =>
    $isSelected ? "var(--soremo-light-gray)" :
    (!$isSelected && $isHover) ? "var(--soremo-light-gray)" :
    "var(--soremo-border)"};
`;

// アイコンの種類の定義
const ICON_TYPES = {
  CHEVRON_RIGHT: "chevron_right",
  MORE_HORIZ: "more_horiz",
  EXPAND_CIRCLE_DOWN: "expand_circle_down",
  EXPAND_CIRCLE_UP: "expand_circle_up",
};

// セクションヘッダーコンポーネント
const SectionHeader = memo(({ name, size, hasBorderBottom }) => (
  <SectionListItem
    $size={size}
    $hasBorderBottom={hasBorderBottom}
  >
    {name}
  </SectionListItem>
));

SectionHeader.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.string.isRequired,
  hasBorderBottom: PropTypes.bool.isRequired,
};

// ステータス表示コンポーネント
const StatusDisplay = ({ status, statusTone, icon, isHover, isSelected }) => (
  <Status $statusTone={statusTone}>
    {status && <div>{status}</div>}
    {icon && <Icon $isHover={isHover} $isSelected={isSelected}>{icon}</Icon>}
  </Status>
);

StatusDisplay.propTypes = {
  status: PropTypes.string,
  statusTone: PropTypes.oneOf(["muted", "accent"]),
  icon: PropTypes.string,
  isHover: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
};

export const ListItem = memo(({
  name,
  size = "default",
  status,
  statusTone = "muted",
  alert,
  onClick,
  hasBorderBottom = true,
  icon = ICON_TYPES.CHEVRON_RIGHT,
  isSelected = false,
  interactive = true,
}) => {
  const [isHover, setIsHover] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsHover(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHover(false);
    if (!isSelected) setIsActive(false);
  }, [isSelected]);

  const handleMouseDown = useCallback(() => {
    if (!isSelected) setIsActive(true);
  }, [isSelected]);

  const handleMouseUp = useCallback(() => {
    if (!isSelected) setIsActive(false);
  }, [isSelected]);

  useEffect(() => {
    // 選択状態が変更された時にホバーとアクティブ状態をリセット
    setIsHover(false);
    setIsActive(false);
  }, [isSelected]);

  // セクションヘッダーの場合
  if (size === "section") {
    return (
      <SectionHeader
        name={name}
        size={size}
        hasBorderBottom={hasBorderBottom}
      />
    );
  }

  // 非インタラクティブ（クリック不可）の場合は、ホバー/カーソルと chevron を出さない。
  // chevron 分の右パディングを確保して status の位置はインタラクティブ時と揃える。
  if (!interactive) {
    return (
      <NonInteractiveListItem $size={size} $hasBorderBottom={hasBorderBottom}>
        <Content $hasAlert={!!alert}>
          <Label>{name}</Label>
          {alert && <AlertText>{alert}</AlertText>}
        </Content>
        <StatusDisplay
          status={status}
          statusTone={statusTone}
          icon={null}
          isHover={false}
          isSelected={false}
        />
      </NonInteractiveListItem>
    );
  }

  // 通常のリストアイテムの場合
  return (
    <InteractiveListItem
      $size={size}
      $hasBorderBottom={hasBorderBottom}
      $isHover={isHover}
      $isActive={isActive}
      $isSelected={isSelected}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <Content $hasAlert={!!alert}>
        <Label>{name}</Label>
        {alert && <AlertText>{alert}</AlertText>}
      </Content>
      <StatusDisplay
        status={status}
        statusTone={statusTone}
        icon={icon}
        isHover={isHover}
        isSelected={isSelected}
      />
    </InteractiveListItem>
  );
});

// PropTypes定義
ListItem.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["default", "compact", "section"]),
  status: PropTypes.string,
  statusTone: PropTypes.oneOf(["muted", "accent"]),
  alert: PropTypes.string,
  onClick: PropTypes.func,
  hasBorderBottom: PropTypes.bool,
  icon: PropTypes.oneOf(Object.values(ICON_TYPES)),
  isSelected: PropTypes.bool,
  interactive: PropTypes.bool,
};

ListItem.displayName = 'ListItem';

export default ListItem;
